import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	CommoditiesFormInput,
	CommoditiesFormOutput,
} from "web_myplann/Portfolio/Forms/CommoditiesForm/commoditiesFormSchema";
import { useProductCategoryOptions } from "web_myplann/Portfolio/Helpers/useProductCategoryOptions";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type CommoditiesFormFieldsProps = {
	control: Control<CommoditiesFormInput, unknown, CommoditiesFormOutput>;
};

export const CommoditiesFormFields = ({
	control,
}: CommoditiesFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const productCategoryOptions = useProductCategoryOptions(
		ClientPortfolioDomainEnum.Commodities,
	);

	return (
		<>
			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddCommodities.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddCommodities.Fields.name)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(Resources.Portfolio.AddCommodities.Fields.contractNumber)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddCommodities.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddCommodities.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormSelect
					control={control}
					name="productCategoryId"
					label={t(Resources.Portfolio.AddCommodities.Fields.productCategory)}
					options={productCategoryOptions}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddCommodities.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddCommodities.Fields.signedAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddCommodities.Fields.startAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddCommodities.Fields.endAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddCommodities.Sections.note)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="note"
					label={t(Resources.Portfolio.AddCommodities.Fields.note)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					rows={1}
					isLast
				/>
			</Stack>
		</>
	);
};
